import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { FileText, Shield, CheckCircle, Tag, Loader2 } from "lucide-react";
import { PublicClientApplication } from "@azure/msal-browser";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// MSAL configuration for Microsoft Entra ID
// Note: These are placeholder values - users need to configure their own Azure AD app
const msalConfig = {
  auth: {
    clientId: process.env.REACT_APP_AZURE_CLIENT_ID || "",
    authority: `https://login.microsoftonline.com/${process.env.REACT_APP_AZURE_TENANT_ID || "common"}`,
    redirectUri: typeof window !== 'undefined' ? window.location.origin + "/login" : "",
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false,
  }
};

// Initialize MSAL instance only if Azure is configured
let msalInstance = null;
if (msalConfig.auth.clientId) {
  try {
    msalInstance = new PublicClientApplication(msalConfig);
  } catch (e) {
    console.warn("MSAL initialization failed:", e);
  }
}

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const isSignupPath = location.pathname === "/signup";
  const hasProcessedSSO = useRef(false);
  
  const [isLogin, setIsLogin] = useState(!isSignupPath);
  const [loading, setLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [validatingCode, setValidatingCode] = useState(false);
  const [referralInfo, setReferralInfo] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    entityName: "",
    referralCode: "",
  });

  const { login, register, user, loginWithSSO } = useAuth();
  const navigate = useNavigate();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleSSOCallback = useCallback(async (sessionId) => {
    setSsoLoading(true);
    try {
      const response = await axios.post(`${API}/sso/google/session`, {
        session_id: sessionId
      });
      
      const { token, user: userData } = response.data;
      
      // Store token and login
      if (loginWithSSO) {
        await loginWithSSO(token, userData);
      } else {
        localStorage.setItem("token", token);
        window.location.href = "/";
      }
      
      // Clear hash from URL
      window.history.replaceState(null, "", window.location.pathname);
      
      toast.success(`Welcome, ${userData.name}!`);
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "SSO authentication failed");
      window.history.replaceState(null, "", window.location.pathname);
    } finally {
      setSsoLoading(false);
    }
  }, [loginWithSSO, navigate]);

  // Handle SSO callback - check URL fragment for session_id
  useEffect(() => {
    if (hasProcessedSSO.current) return;
    
    const hash = window.location.hash;
    if (hash && hash.includes("session_id=")) {
      hasProcessedSSO.current = true;
      
      const sessionId = hash.split("session_id=")[1]?.split("&")[0];
      if (sessionId) {
        handleSSOCallback(sessionId);
      }
    }
  }, [handleSSOCallback]);

  const handleGoogleSSO = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/login";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleMicrosoftSSO = async () => {
    if (!msalInstance) {
      toast.error("Microsoft SSO is not configured. Please contact your administrator.");
      return;
    }
    
    setSsoLoading(true);
    try {
      // Initialize MSAL if needed
      await msalInstance.initialize();
      
      // Login with popup
      const loginResponse = await msalInstance.loginPopup({
        scopes: ["openid", "profile", "email", "User.Read"],
      });
      
      if (loginResponse && loginResponse.accessToken) {
        // Exchange token with backend
        const response = await axios.post(`${API}/sso/azure/token`, {
          access_token: loginResponse.accessToken,
          tenant_id: loginResponse.tenantId
        });
        
        const { token, user: userData } = response.data;
        
        // Store token and login
        if (loginWithSSO) {
          await loginWithSSO(token, userData);
        } else {
          localStorage.setItem("token", token);
        }
        
        toast.success(`Welcome, ${userData.name}!`);
        navigate("/");
      }
    } catch (error) {
      if (error.errorCode === "user_cancelled") {
        // User cancelled the login
        return;
      }
      console.error("Microsoft SSO error:", error);
      toast.error(error.response?.data?.detail || "Microsoft authentication failed");
    } finally {
      setSsoLoading(false);
    }
  };

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate("/");
    }
  }, [user, navigate]);

  // Check for referral code in URL
  useEffect(() => {
    const refCode = searchParams.get("ref");
    if (refCode) {
      setFormData(prev => ({ ...prev, referralCode: refCode }));
      setIsLogin(false); // Switch to signup mode
      validateReferralCode(refCode);
    }
  }, [searchParams]);

  const validateReferralCode = async (code) => {
    if (!code || code.length < 3) {
      setReferralInfo(null);
      return;
    }
    
    setValidatingCode(true);
    try {
      const response = await axios.get(`${API}/referrals/validate/${code.toUpperCase()}`);
      setReferralInfo(response.data);
    } catch (error) {
      setReferralInfo(null);
      if (code.length >= 4) {
        toast.error("Invalid or expired referral code");
      }
    } finally {
      setValidatingCode(false);
    }
  };

  const handleReferralCodeChange = (value) => {
    setFormData({ ...formData, referralCode: value.toUpperCase() });
    // Debounce validation
    clearTimeout(window.referralValidationTimeout);
    window.referralValidationTimeout = setTimeout(() => {
      validateReferralCode(value);
    }, 500);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const userData = await login(formData.email, formData.password);
        toast.success("Welcome back!");
        
        // Navigate based on role
        if (userData.role === "platform_admin") {
          navigate("/platform");
        } else if (userData.role === "sales_director") {
          navigate("/referrals");
        } else {
          navigate("/");
        }
      } else {
        const userData = await register(
          formData.name,
          formData.email,
          formData.password,
          formData.entityName,
          formData.referralCode || null
        );
        
        if (referralInfo) {
          toast.success(`Account created with ${referralInfo.discount_percent}% discount!`);
        } else {
          toast.success("Account created successfully!");
        }
        navigate("/");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex">
      {/* Left side - Hero */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1685602729277-54538940a06c?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Corporate boardroom"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-primary-700/80" />
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          <h1 className="font-heading text-4xl font-semibold mb-4">
            MyMeetingsPal
          </h1>
          <p className="text-xl text-primary-100 mb-8">
            Corporate Minutes Generator
          </p>
          
          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-white/10 rounded-lg">
                <FileText className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-medium mb-1">Professional Minutes</h3>
                <p className="text-sm text-primary-100">
                  Generate compliant corporate minutes with formal governance language
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-4">
              <div className="p-2 bg-white/10 rounded-lg">
                <Shield className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-medium mb-1">Audit Trail</h3>
                <p className="text-sm text-primary-100">
                  Full transcription records for governance compliance
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-4">
              <div className="p-2 bg-white/10 rounded-lg">
                <CheckCircle className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-medium mb-1">Export Ready</h3>
                <p className="text-sm text-primary-100">
                  Download minutes in DOCX or PDF format
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8 lg:hidden">
            <h1 className="font-heading text-2xl font-semibold text-primary-700">
              MyMeetingsPal
            </h1>
            <p className="text-stone-500 mt-1">Corporate Minutes Generator</p>
          </div>

          {/* Referral Banner */}
          {referralInfo && !isLogin && (
            <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-full">
                  <Tag className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-emerald-800">
                    {referralInfo.discount_percent}% Discount Applied!
                  </p>
                  <p className="text-xs text-emerald-600">
                    Referred by {referralInfo.sales_director_name}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white border border-stone-200 rounded-lg p-8 shadow-card">
            <h2 className="font-heading text-2xl font-semibold text-stone-900 mb-2">
              {isLogin ? "Sign in" : "Create account"}
            </h2>
            <p className="text-stone-500 mb-6">
              {isLogin
                ? "Enter your credentials to access your meetings"
                : "Set up your organization to get started"}
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <>
                  <div>
                    <Label htmlFor="name" className="text-stone-700">
                      Full Name
                    </Label>
                    <Input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      placeholder="John Smith"
                      className="mt-1"
                      required
                      data-testid="register-name-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="entityName" className="text-stone-700">
                      Organization Name
                    </Label>
                    <Input
                      id="entityName"
                      type="text"
                      value={formData.entityName}
                      onChange={(e) =>
                        setFormData({ ...formData, entityName: e.target.value })
                      }
                      placeholder="Acme Corporation"
                      className="mt-1"
                      required
                      data-testid="register-entity-input"
                    />
                  </div>
                  
                  {/* Referral Code Field */}
                  <div>
                    <Label htmlFor="referralCode" className="text-stone-700">
                      Referral Code <span className="text-stone-400">(optional)</span>
                    </Label>
                    <div className="relative mt-1">
                      <Input
                        id="referralCode"
                        type="text"
                        value={formData.referralCode}
                        onChange={(e) => handleReferralCodeChange(e.target.value)}
                        placeholder="e.g., SAVE20"
                        className="font-mono uppercase"
                        data-testid="register-referral-input"
                      />
                      {validatingCode && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <Loader2 className="w-4 h-4 text-stone-400 animate-spin" />
                        </div>
                      )}
                      {referralInfo && !validatingCode && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                        </div>
                      )}
                    </div>
                    {referralInfo && (
                      <p className="text-xs text-emerald-600 mt-1">
                        Valid code! {referralInfo.discount_percent}% discount will be applied.
                      </p>
                    )}
                  </div>
                </>
              )}

              <div>
                <Label htmlFor="email" className="text-stone-700">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  placeholder="john@company.com"
                  className="mt-1"
                  required
                  data-testid="login-email-input"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-stone-700">
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  placeholder="Enter your password"
                  className="mt-1"
                  required
                  data-testid="login-password-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-primary-700 hover:bg-primary-700/90"
                disabled={loading || ssoLoading}
                data-testid="login-submit-btn"
              >
                {loading
                  ? "Please wait..."
                  : isLogin
                  ? "Sign in"
                  : "Create account"}
              </Button>
            </form>

            {/* SSO Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-stone-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-white px-4 text-stone-500">Or continue with</span>
              </div>
            </div>

            {/* SSO Buttons */}
            <div className="space-y-3">
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleGoogleSSO}
                disabled={ssoLoading}
                data-testid="google-sso-btn"
              >
                {ssoLoading ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                )}
                Continue with Google
              </Button>
              
              {/* Microsoft SSO Button - only show if configured */}
              {msalConfig.auth.clientId && (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={handleMicrosoftSSO}
                  disabled={ssoLoading}
                  data-testid="microsoft-sso-btn"
                >
                  {ssoLoading ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path fill="#F25022" d="M1 1h10v10H1z"/>
                      <path fill="#00A4EF" d="M1 13h10v10H1z"/>
                      <path fill="#7FBA00" d="M13 1h10v10H13z"/>
                      <path fill="#FFB900" d="M13 13h10v10H13z"/>
                    </svg>
                  )}
                  Continue with Microsoft
                </Button>
              )}
            </div>

            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => {
                  setIsLogin(!isLogin);
                  setReferralInfo(null);
                  setFormData(prev => ({ ...prev, referralCode: "" }));
                }}
                className="text-sm text-primary-700 hover:underline"
                data-testid="toggle-auth-mode"
              >
                {isLogin
                  ? "Don't have an account? Sign up"
                  : "Already have an account? Sign in"}
              </button>
            </div>
            
            {isLogin && (
              <div className="mt-4 text-center">
                <a
                  href="/pricing"
                  className="text-sm text-stone-500 hover:text-primary-700"
                >
                  View pricing plans
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
